import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { searchContractorApplications } from 'services/apis'
import { Input2 } from 'stories/components'

export type ExistingContractorOption = {
  no: number
  loanNumber: number
  firstName: string
  lastName: string
  entityTitle: string
  entityEmail: string
  approveContractor: boolean
  status: string
}

export function ExistingContractorSearch({
  excludeEmails,
  onSelect,
}: {
  excludeEmails: string[]
  onSelect: (item: ExistingContractorOption) => void
}) {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<ExistingContractorOption[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handle = setTimeout(() => {
      load(query, false)
    }, 300)
    return () => clearTimeout(handle)
  }, [query])

  const load = async (search: string, show: boolean) => {
    setLoading(true)
    try {
      const rows = (await searchContractorApplications(search)) as ExistingContractorOption[]
      const blocked = new Set(excludeEmails.filter(Boolean).map((e) => e.toLowerCase()))
      setResults((Array.isArray(rows) ? rows : []).filter((row) => !blocked.has((row.entityEmail || '').toLowerCase())))
      if (show) setOpen(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative mb-4">
      <Input2
        title="Select existing contractor"
        type="search"
        value={query}
        placeholder="Search by name, company, email, or contractor no"
        onChange={(v) => setQuery(v)}
        onFocus={() => {
          if (!results.length) load(query, true)
          else setOpen(true)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (
        <div className="absolute z-20 w-full bg-white border border-gray-200 shadow rounded mt-1 max-h-72 overflow-auto">
          <LayoutLoading show={loading} />
          {!loading && !results.length && (
            <div className="px-3 py-2 text-sm text-gray-500 italic">No matching contractors on other loans.</div>
          )}
          {results.map((row) => (
            <button
              type="button"
              key={row.no}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b last:border-b-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(row)
                setQuery('')
                setOpen(false)
              }}
            >
              <div className="font-variation-settings-600 text-sm">
                {row.entityTitle || `${row.firstName} ${row.lastName}`}
              </div>
              <div className="text-xs text-gray-600">
                {row.firstName} {row.lastName}
                {row.entityEmail ? ` · ${row.entityEmail}` : ''}
                {` · #${row.no}`}
                {row.loanNumber ? ` · Loan ${row.loanNumber}` : ''}
                {row.approveContractor ? ' · Approved' : ''}
                {row.status ? ` · ${row.status}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
